/**
 * Die Ziffernfelder von „Stand erfassen" und „Zähler getauscht" (Spec 46b,
 * Fachregel 15) — reine Logik, dieselbe wie `MeterReadingDraft` auf iOS.
 *
 * `typed` ist die Folge der getippten Ziffern (Länge 0…N, N = Stellenzahl).
 * Eingaben hängen rechts an, die Rücktaste nimmt rechts weg; angezeigt wird
 * `typed` links mit Nullen aufgefüllt. Ohne Eingabe zeigen die Zellen den
 * letzten Stand als grauen Platzhalter. Rechnen und Prüfen bleibt am Server
 * (Spec 46a, Regel 9) — hier steht nur, wie Ziffern in Zellen wandern.
 */

type CellKind = 'placeholder' | 'padding' | 'typed'

export interface DigitCell {
  readonly digit: string
  readonly kind: CellKind
}

/** Kein letzter Stand: ein Strich je Zelle. */
const NO_DIGIT = '–'

/** Hängt eine Ziffer rechts an; ist `typed` voll, wird sie verworfen. */
export function appendDigit(typed: string, digit: string, digits: number): string {
  if (!/^[0-9]$/.test(digit) || typed.length >= digits) return typed
  return typed + digit
}

/** Rücktaste: nimmt rechts eine Ziffer weg. */
export function deleteDigit(typed: string): string {
  return typed.slice(0, -1)
}

/** Ein Eingabefeld-Inhalt (Tippen, Einfügen, Diktat) als `typed`: nur Ziffern,
 * Überhang rechts verworfen — dieselbe Regel wie Ziffer für Ziffer. */
export function typedFromInput(raw: string, digits: number): string {
  return raw.split('').reduce((typed, char) => appendDigit(typed, char, digits), '')
}

/** `"023432"` → 23432, leer → `null`. */
export function valueOf(typed: string): number | null {
  return typed === '' ? null : Number(typed)
}

/** Die N Zellen: Platzhalter (letzter Stand, grau), aufgefüllte Nullen (grau)
 * und getippte Ziffern. */
export function cells(typed: string, digits: number, lastValue: number | null): DigitCell[] {
  if (typed === '') {
    const placeholder = lastValue === null ? NO_DIGIT.repeat(digits) : String(lastValue).padStart(digits, '0')
    return placeholder.split('').map((digit) => ({ digit, kind: 'placeholder' }))
  }
  const padded = typed.padStart(digits, '0')
  const firstTyped = digits - typed.length
  return padded.split('').map((digit, index) => ({ digit, kind: index < firstTyped ? 'padding' : 'typed' }))
}

/** Web liest kein Foto aus: mit Beleg `photo`, sonst `manual` (Regel 15). */
export function sourceFor({ photo }: { readonly photo: boolean }): 'photo' | 'manual' {
  return photo ? 'photo' : 'manual'
}
